/**
 * Custom hooks for resource data fetching
 * Provides reusable hooks with loading states, error handling, and caching
 */

import { useState, useEffect, useCallback } from 'react';
import { ResourceQueryOptions } from '../services/resources.service';
import { useToast } from '../contexts/ToastContext';

/**
 * Permission-denied responses (HTTP 403 / PolicyFailed) mean the user's
 * role simply lacks access to that resource — not a malfunction. Reads
 * fail silently with empty data instead of error banners/toast spam,
 * while writes still surface a clear "no permission" message.
 */
function isPermissionDeniedError(err: any): boolean {
  const status = err?.status ?? err?.error?.status ?? err?.data?.error?.status;
  return status === 403 || err?.error?.name === 'PolicyFailed' || err?.data?.error?.name === 'PolicyFailed';
}

function mutationErrorMessage(err: any, error: Error, fallback: string): string {
  if (isPermissionDeniedError(err)) {
    return 'You do not have permission to perform this action';
  }
  // Strapi ValidationError bodies carry field-level detail in
  // data.error.details.errors — "code must be unique" beats "2 errors occurred".
  const details = err?.data?.error?.details?.errors;
  if (Array.isArray(details) && details.length) {
    const fieldMsgs = details
      .map((d: any) => d?.message || (Array.isArray(d?.path) ? d.path.join('.') : null))
      .filter(Boolean);
    if (fieldMsgs.length) return fieldMsgs.slice(0, 3).join(' · ');
  }
  return err.error?.message || error.message || fallback;
}

// Generic resource state
export interface ResourceState<T> {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

// Single item state
export interface SingleResourceState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

// Mutation state
export interface MutationState<T> {
  isLoading: boolean;
  error: Error | null;
  execute: (data: any) => Promise<T | void>;
}

/**
 * Generic hook for fetching a collection of resources
 */
export function useResourceCollection<T>(
  fetchFn: (options?: ResourceQueryOptions) => Promise<{ data: T[]; meta?: any }>,
  options?: ResourceQueryOptions,
  shouldShowToastOnError = true
): ResourceState<T> & { meta?: any } {
  const [data, setData] = useState<T[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();

  // Stringify options to prevent infinite loop from object reference changes
  const optionsKey = JSON.stringify(options);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchFn(options);
      setData(response.data);
      setMeta(response.meta);
    } catch (err: any) {
      if (isPermissionDeniedError(err)) {
        setData([]);
        setMeta(null);
        setError(null);
      } else {
        const error = err instanceof Error ? err : new Error('Failed to fetch data');
        setError(error);
        if (shouldShowToastOnError) {
          toast.error(err.error?.message || error.message || 'Failed to load data');
        }
      }
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFn, optionsKey, shouldShowToastOnError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    meta,
    isLoading,
    error,
    refetch: fetchData,
  };
}

/**
 * Generic hook for fetching a single resource by ID
 */
export function useResourceById<T>(
  fetchFn: (id: number | string, options?: ResourceQueryOptions) => Promise<T>,
  id: number | string | null,
  options?: ResourceQueryOptions,
  shouldShowToastOnError = true
): SingleResourceState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();

  // Stringify options to prevent infinite loop from object reference changes
  const optionsKey = JSON.stringify(options);

  const fetchData = useCallback(async () => {
    if (!id) {
      setData(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchFn(id, options);
      setData(response);
    } catch (err: any) {
      if (isPermissionDeniedError(err)) {
        setData(null);
        setError(null);
      } else {
        const error = err instanceof Error ? err : new Error('Failed to fetch data');
        setError(error);
        if (shouldShowToastOnError) {
          toast.error(err.error?.message || error.message || 'Failed to load data');
        }
      }
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFn, id, optionsKey, shouldShowToastOnError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

/**
 * Generic hook for creating a resource
 */
export function useCreateResource<T>(
  createFn: (data: any) => Promise<T>,
  onSuccess?: (data: T) => void,
  shouldShowToast = true
): MutationState<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();

  const execute = useCallback(
    async (data: any): Promise<T> => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await createFn(data);
        if (shouldShowToast) {
          toast.success('Created successfully');
        }
        if (onSuccess) {
          onSuccess(result);
        }
        return result;
      } catch (err: any) {
        const error = err instanceof Error ? err : new Error('Failed to create');
        setError(error);
        if (shouldShowToast) {
          toast.error(mutationErrorMessage(err, error, 'Failed to create'));
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createFn, onSuccess, shouldShowToast]
  );

  return {
    isLoading,
    error,
    execute,
  };
}

/**
 * Generic hook for updating a resource
 */
export function useUpdateResource<T>(
  updateFn: (id: number | string, data: any) => Promise<T>,
  onSuccess?: (data: T) => void,
  shouldShowToast = true
): MutationState<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();

  const execute = useCallback(
    async ({ id, data }: { id: number | string; data: any }): Promise<T> => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await updateFn(id, data);
        if (shouldShowToast) {
          toast.success('Updated successfully');
        }
        if (onSuccess) {
          onSuccess(result);
        }
        return result;
      } catch (err: any) {
        const error = err instanceof Error ? err : new Error('Failed to update');
        setError(error);
        if (shouldShowToast) {
          toast.error(mutationErrorMessage(err, error, 'Failed to update'));
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateFn, onSuccess, shouldShowToast]
  );

  return {
    isLoading,
    error,
    execute,
  };
}

/**
 * Generic hook for deleting a resource
 */
export function useDeleteResource(
  deleteFn: (id: number | string) => Promise<void>,
  onSuccess?: () => void,
  shouldShowToast = true
): MutationState<void> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();

  const execute = useCallback(
    async (id: number | string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        await deleteFn(id);
        if (shouldShowToast) {
          toast.success('Deleted successfully');
        }
        if (onSuccess) {
          onSuccess();
        }
      } catch (err: any) {
        const error = err instanceof Error ? err : new Error('Failed to delete');
        setError(error);
        if (shouldShowToast) {
          toast.error(mutationErrorMessage(err, error, 'Failed to delete'));
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteFn, onSuccess, shouldShowToast]
  );

  return {
    isLoading,
    error,
    execute,
  };
}

